<template>
  <div class="w-full p-6">
    <!-- Header Section -->
    <div class="mb-6">
      <div class="flex justify-between items-center mb-2">
        <h1 class="text-2xl font-bold text-primary">Dashboard</h1>
        <div>
          <button
            v-if="canTriggerForecast"
            @click="openNewForecastModal"
            class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            New Forecast
          </button>
        </div>
      </div>
      <p class="text-muted-foreground">Overview of tasks, activities, and recent updates</p>
    </div>

    <!-- Reporting Periods Section -->
    <div class="mb-6">
      <div v-if="forecastingProcesses.length > 0" class="space-y-4">
        <div v-for="process in forecastingProcesses" :key="process.id" class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-bold">{{ process.name }} Reporting Period</h2>
          <p class="text-gray-600 mb-2">
            {{ formatDate(process.start_date) }} - {{ formatDate(process.end_date) }}
            <span v-if="getDaysRemaining(process.end_date) > 0">
              ({{ getDaysRemaining(process.end_date) }} days remaining)
            </span>
            <span v-else class="text-red-500">
              (Overdue)
            </span>
          </p>
          <div class="w-full bg-gray-200 rounded-full h-2.5">
            <div
              class="bg-blue-600 h-2.5 rounded-full"
              :style="{ width: getProgressPercentage(process.start_date, process.end_date) + '%' }"
            ></div>
          </div>
        </div>
      </div>
      <div v-else class="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
        <div class="flex flex-col items-center">
          <div class="mb-4 p-3 bg-gray-100 rounded-full">
            <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Active Reporting Periods</h3>
          <p class="text-gray-500 mb-6">Start a new forecast process to begin tracking reporting periods</p>
          <button
            @click="openNewForecastModal"
            v-if="canTriggerForecast"
            class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Start New Forecast
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Tasks Section -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-semibold">Tasks</h2>
              <p class="text-sm text-gray-500">Manage and track reporting tasks</p>
            </div>
            <button class="p-2 rounded-md hover:bg-gray-100">
              <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>

          <TaskView
            :myTasks="myTasks"
            :allTasks="openTasks"
            :reminders="reminders"
            :overdueRemindersCount="overdueRemindersCount"
            :canViewReminders="canViewReminders"
          />
        </div>
      </div>         <!-- Task Content -->

      <!-- Activity Feed Section -->
      <div class="lg:col-span-1">
        <ActivityFeed :activities="activities" />
      </div>
    </div>
  </div>

  <!-- New Forecast Modal -->
  <div v-if="showForecastModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
      <!-- Close button -->
      <button
        @click="showForecastModal = false"
        class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        <span class="text-xl">×</span>
      </button>
      
      <h2 class="text-xl font-bold mb-2">Create New Reporting Period</h2>
      <p class="text-gray-600 mb-6">Set up a new reporting period by providing a name and date range.</p>
      
      <div class="mb-4">
        <label for="periodName" class="block text-sm font-medium text-gray-700 mb-1">Period Name</label>
        <input
          type="text"
          id="periodName"
          v-model="periodName"
          placeholder="e.g. Q3 2025"
          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
      </div>
      
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="startDate" class="block text-sm text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              id="startDate"
              v-model="startDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
          <div>
            <label for="endDate" class="block text-sm text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              id="endDate"
              v-model="endDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
        </div>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showForecastModal = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="confirmCreateForecast"
          class="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Create Period</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Confirmation Dialog -->
  <div v-if="showConfirmDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
      <h3 class="text-lg font-medium mb-3">Confirm Forecast Creation</h3>
      <p class="text-gray-600 mb-6">Are you sure you want to create a new forecasting process? This action cannot be undone.</p>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showConfirmDialog = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="startForecastProcess"
          class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Yes, Create</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUnmounted, watch, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import { BusinessRole } from '@/types/businessRole';
import { ProcessStatus } from '@/types/processStatus';
import { TaskDocProfile } from '@/features/task-management';
import { DOCUMENT_STATUS, REPORT_TYPE } from '@/types/document';
import { useDueDates } from '@/composables/useDueDates';
import { ACTIONS } from '@/types/taskActions';
import TaskView from './tasks/TaskView.vue';
import ActivityFeed from './tasks/ActivityFeed.vue';
import { DocumentService } from '@/features/document-management';
import { TaskService } from '@/features/task-management';

// No longer needed as we're using the getDueDate function

export default defineComponent({
  name: 'DashboardView',
  components: {
    TaskView,
    ActivityFeed,
  },
  setup() {
    // Auth
    const { user, profile, init } = useAuth();
    
    // Initialize services
    const documentService = new DocumentService();
    const taskService = new TaskService();
    
    // Function to load tasks
    const loadTasks = async () => {
      try {
        const data = await taskService.fetchTasks();
        allTasks.value = data || [];
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    // Initialize auth state
    onMounted(async () => {
      await init();
      await loadTasks();
      
      // Add event listener for task refresh
      window.addEventListener('refresh-tasks', loadTasks);
    });
    
    // Clean up event listener
    onUnmounted(() => {
      window.removeEventListener('refresh-tasks', loadTasks);
    });
    
    // Check if user has Head of PCO role
    const canTriggerForecast = computed(() => {
      return true || profile.value?.business_role === BusinessRole.HEAD_OF_PCO;
    });

    // Computed property for all open tasks - tasks where done_at is null
    const openTasks = computed(() => {
      return allTasks.value.filter(task => task.done_at === null);
    });

    // Computed property to filter tasks for the current user
    const myTasks = computed(() => {
      const userId = user.value?.id;
      if (!userId) return [];

      return allTasks.value.filter(task =>
        task.assigned_user_id === userId && task.done_at === null
      );
    });

    // Computed property for reminders - tasks where done_at is null and action is 'DOWNLOAD'
    const reminders = computed(() => {
      return allTasks.value.filter(task =>
        task.done_at === null && task.action === 'DOWNLOAD'
      );
    });

    // Computed property for activities - tasks with done_at in the past
    const activities = computed(() => {
      const now = new Date();

      return allTasks.value.filter(task => {
        // Check if done_at exists and is not null
        if (!task.done_at) return false;

        // Compare the done_at date with current date
        const doneDate = new Date(task.done_at);
        return doneDate < now;
      });
    });

    // Computed property for overdue reminders count
    const overdueRemindersCount = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return reminders.value.filter(reminder => {
        if (!reminder.due_at) return false;
        const dueDate = new Date(reminder.due_at);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      }).length;
    });

    // We don't need fetchTasks from useTasks anymore as we're using taskService
    // const { fetchTasks } = useTasks();

    // Check if user has HEAD_OF_PCO or CONTROLLER role for viewing reminders
    const canViewReminders = computed(() => {
      return profile.value?.business_role === BusinessRole.HEAD_OF_PCO ||
             profile.value?.business_role === BusinessRole.CONTROLLER;
    });
    
    // Reset activeTab if user doesn't have permission to view Reminders
    watch(canViewReminders, (hasPermission) => {
      if (!hasPermission && activeTab.value === 'reminders') {
        activeTab.value = 'my-tasks';
      }
    });


    // Store for all forecasting processes
    const forecastingProcesses = ref<any[]>([]);
    
    // Modal state
    const showForecastModal = ref(false);
    const showConfirmDialog = ref(false);
    
    // Process state
    const processStatus = ref<string | null>(null); // NULL, 'PENDING', 'PROCESSING', 'COMPLETED', 'ERROR'
    const processId = ref<string | null>(null);
    const statusPollingInterval = ref<number | null>(null);
    const isLoading = ref(false);
    
    // Form data
    const periodName = ref('');
    const startDate = ref('');
    const endDate = ref('');
    
    // Simple notification function (can be replaced with a proper notification system later)
    const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
      console.log(`[${type.toUpperCase()}] ${message}`);
      // In a real implementation, this would show a UI notification
    };
    
    // Open the modal
    const openNewForecastModal = () => {
      // Reset form data
      periodName.value = '';
      startDate.value = '';
      endDate.value = '';
      showForecastModal.value = true;
    };
    
    // Show confirmation dialog
    const confirmCreateForecast = () => {
      showConfirmDialog.value = true;
    };
    
    // Function to start polling for status updates
    const startStatusPolling = (id: string) => {
      // Clear any existing interval
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
      
      // Set up polling every 5 seconds
      statusPollingInterval.value = window.setInterval(async () => {
        try {
          const { data, error } = await supabase
            .from('forecasting_process')
            .select('id, status')
            .eq('id', id)
            .single();
            
          if (error) throw error;
          
          processStatus.value = data.status;
          
          // If process is completed or errored, stop polling
          if (data.status === ProcessStatus.CREATED || data.status === ProcessStatus.COMPLETED) {
            if (statusPollingInterval.value) {
              clearInterval(statusPollingInterval.value);
            }
            
            // Show appropriate notification
            if (data.status === ProcessStatus.COMPLETED) {
              showNotification('Forecast process completed successfully');
            }
            
            // Refresh the list of forecasting processes
            fetchForecastingProcesses();
          }
        } catch (error) {
          console.error('Error polling for status:', error);
          if (statusPollingInterval.value) {
            clearInterval(statusPollingInterval.value);
          }
        }
      }, 5000);
    };
    
    /**
     * Create a document and associated task for a forecasting process
     * @param processId The ID of the forecasting process
     * @param processName The name of the forecasting process
     * @param userId The ID of the user to assign the document and task to
     * @returns The created document ID or null if there was an error
     */
    const createDocumentAndTask = async (processId: string, processName: string, userId: string) => {
      try {
        // 1. Create the document using DocumentService
        const documentData = await documentService.createDocument({
          name: `${processName} - Calendar`,
          status: DOCUMENT_STATUS.CREATING,
          report_type: REPORT_TYPE.CAL,
          created_by: userId,
          forecast_id: processId
        });
        
        // 2. Calculate due date using the getDueDate function
        const { getDueDate } = useDueDates();
        const dueDateString = await getDueDate(ACTIONS.UPLOAD, documentData.id);
        
        // 3. Create the task using TaskService
        const taskResult = await taskService.createTask({
          due_at: dueDateString,
          action: 'UPLOAD',
          assigned_user_id: userId,
          document_id: documentData.id
        });
        
        if (!taskResult) {
          console.error('Error creating task');
          throw new Error('Failed to create task');
        }
        
        return documentData.id;
      } catch (error) {
        console.error('Error in createDocumentAndTask:', error);
        return null;
      }
    };
    
    // Start the forecast process
    const startForecastProcess = async () => {
      // Set loading state
      isLoading.value = true;
      
      try {
        if (!user.value) {
          // showNotification('User not authenticated', 'error');
          // return;
        }

        // Format dates as ISO strings for timestamp
        const formattedStartDate = startDate.value ? new Date(startDate.value).toISOString() : null;
        const formattedEndDate = endDate.value ? new Date(endDate.value).toISOString() : null;
        
        // 1. Create the forecasting process
        const { data, error } = await supabase
          .from('forecasting_process')
          .insert({
            name: periodName.value,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            status: ProcessStatus.CREATED
          })
          .select();
          
        if (error) throw error;
        
        const newProcessId = data[0].id;
        
        // Process the response
        processId.value = newProcessId;
        processStatus.value = ProcessStatus.CREATED;
        
        // Close confirmation dialog but keep modal open
        showConfirmDialog.value = false;

        // For the moment, we do not have the planners yet, so this is mock code
        // It will create two documents and assign them to the current user himself
        const { data: userData } = await supabase.auth.getSession()
        const currentUserId = userData?.session?.user.id
        
        // Ensure we have a valid user ID
        if (!currentUserId) {
          console.error('No user ID available, cannot create documents');
          throw new Error('User ID not available');
        }
        
        const responsiblePlannerIds = [currentUserId, currentUserId];
        
        // Create documents for each planner using DocumentService
        const documentPromises = responsiblePlannerIds.map(responsiblePlannerId =>
          documentService.createDocument({
            name: `${periodName.value} - Template`,
            status: DOCUMENT_STATUS.CREATING,
            created_by: responsiblePlannerId,
            forecast_id: newProcessId
          })
        );
        
        try {
          await Promise.all(documentPromises);
        } catch (error) {
          console.error('Error creating documents:', error);
          throw error;
        }
        
        // Create the calendar document and associated task
        if (currentUserId) {
          await createDocumentAndTask(newProcessId, periodName.value, currentUserId);
        } else {
          console.error('User ID not available, cannot create document and task');
        }
        
        showNotification('Forecast process started successfully');
        
        // Start polling for status updates
        startStatusPolling(newProcessId);
        
        // Fetch updated data from backend
        await fetchForecastingProcesses();
        
        // Refresh tasks to include the newly created task
        await loadTasks();
        
        // Now close the modal
        showForecastModal.value = false;
      } catch (error) {
        console.error('Error starting forecast process:', error);
        processStatus.value = 'error';
        showNotification('Failed to start forecast process', 'error');
      } finally {
        // Reset loading state
        isLoading.value = false;
      }
    };
    
    // Function to fetch all forecasting processes
    const fetchForecastingProcesses = async () => {
      try {
        const { data, error } = await supabase
          .from('forecasting_process')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        forecastingProcesses.value = data || [];
        
        // If there's an active process, update the status indicator
        const activeProcess = data?.find(p => p.status === ProcessStatus.CREATED);
        if (activeProcess) {
          processId.value = activeProcess.id;
          processStatus.value = activeProcess.status;
          
          // Start polling for status updates
          startStatusPolling(activeProcess.id);
        }
      } catch (error) {
        console.error('Error fetching forecasting processes:', error);
      }
    };
    
    // Helper function to format dates
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };
    
    // Helper function to calculate days remaining
    const getDaysRemaining = (endDateString: string) => {
      if (!endDateString) return 0;
      
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    };
    
    // Helper function to calculate progress percentage
    const getProgressPercentage = (startDateString: string, endDateString: string) => {
      if (!startDateString || !endDateString) return 0;
      
      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const totalDuration = endDate.getTime() - startDate.getTime();
      const elapsedDuration = today.getTime() - startDate.getTime();
      
      if (totalDuration <= 0) return 100;
      
      let percentage = (elapsedDuration / totalDuration) * 100;
      
      // Ensure percentage is between 0 and 100
      percentage = Math.max(0, Math.min(100, percentage));
      
      return percentage;
    };
    
    // Fetch forecasting processes on component mount
    fetchForecastingProcesses();
    
    // Clean up on component unmount
    onUnmounted(() => {
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
    });
    
    // Active tab state
    const activeTab = ref('my-tasks');
    
    // Track which task is expanded
    const expandedTaskId = ref<string | null>(null);
    
    // Function to toggle task expansion
    const toggleTaskExpanded = (taskId: string) => {
      if (expandedTaskId.value === taskId) {
        // If clicking on the already expanded task, collapse it
        expandedTaskId.value = null;
      } else {
        // Otherwise, expand the clicked task
        expandedTaskId.value = taskId;
      }
    };
    
    // All tasks will be populated from the database
    const allTasks = ref<TaskDocProfile[]>([]);

    
    return {
      activeTab,
      expandedTaskId,
      toggleTaskExpanded,
      myTasks,
      allTasks,
      openTasks,
      canViewReminders,
      reminders,
      activities,
      overdueRemindersCount,
      // Forecast related
      canTriggerForecast,
      showForecastModal,
      showConfirmDialog,
      periodName,
      startDate,
      endDate,
      processStatus,
      isLoading,
      forecastingProcesses,
      formatDate,
      getDaysRemaining,
      getProgressPercentage,
      openNewForecastModal,
      confirmCreateForecast,
      startForecastProcess
    };
  }
});
</script>
